import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaGripVertical, FaTimes } from 'react-icons/fa';

const QuestionSectionModal = ({ show, onHide, onSave, section, mode, activeFormType }) => {
  const [formData, setFormData] = useState({
    formType: activeFormType || '1040',
    sectionTitle: '',
    sectionOrder: 0,
    questions: [{ text: '', order: 0, isRequired: false, helpText: '' }]
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && section) {
      setFormData({
        formType: section.formType,
        sectionTitle: section.sectionTitle,
        sectionOrder: section.sectionOrder,
        questions: section.questions.map(q => ({
          text: q.text,
          order: q.order,
          isRequired: q.isRequired,
          helpText: q.helpText || ''
        }))
      });
    } else {
      setFormData({
        formType: activeFormType || '1040',
        sectionTitle: '',
        sectionOrder: 0,
        questions: [{ text: '', order: 0, isRequired: false, helpText: '' }]
      });
    }
  }, [mode, section, activeFormType, show]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData(prev => ({
      ...prev,
      questions: newQuestions
    }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          text: '',
          order: prev.questions.length,
          isRequired: false,
          helpText: ''
        }
      ]
    }));
  };

  const removeQuestion = (index) => {
    if (formData.questions.length > 1) {
      const newQuestions = formData.questions.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        questions: newQuestions
      }));
    }
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...formData.questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newQuestions.length) {
      [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
      
      // Update order values
      newQuestions[index].order = index;
      newQuestions[targetIndex].order = targetIndex;
      
      setFormData(prev => ({
        ...prev,
        questions: newQuestions
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.sectionTitle.trim()) {
      setError('Section title is required');
      return;
    }

    if (formData.questions.some(q => !q.text.trim())) {
      setError('All questions must have text');
      return;
    }

    // Update question orders
    const questionsWithOrder = formData.questions.map((question, index) => ({
      ...question,
      order: index
    }));

    onSave({
      ...formData,
      questions: questionsWithOrder
    });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onHide}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{mode === 'create' ? 'Create New Section' : 'Edit Section'}</h2>
          <button onClick={onHide} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"><FaTimes /></button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Form Type</label>
                <select value={formData.formType} onChange={(e) => handleInputChange('formType', e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50">
                  <option value="1040">1040</option>
                  <option value="1065">1065</option>
                  <option value="1120-S">1120-S</option>
                  <option value="1120">1120</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section Order</label>
                <input type="number" value={formData.sectionOrder} onChange={(e) => handleInputChange('sectionOrder', parseInt(e.target.value))} min="0" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section Title</label>
              <input type="text" value={formData.sectionTitle} onChange={(e) => handleInputChange('sectionTitle', e.target.value)} placeholder="Enter section title" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-gray-900 dark:text-white">Questions</h5>
                <button type="button" onClick={addQuestion} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600"><FaPlus /> Add Question</button>
              </div>
              <div className="space-y-3">
                {formData.questions.map((question, index) => (
                  <div key={index} className="p-4 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2"><FaGripVertical className="text-gray-400" /><span className="font-medium text-gray-900 dark:text-white">Question {index + 1}</span></div>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveQuestion(index, 'up')} disabled={index === 0} className="p-1.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-dark-600 rounded disabled:opacity-30">↑</button>
                        <button type="button" onClick={() => moveQuestion(index, 'down')} disabled={index === formData.questions.length - 1} className="p-1.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-dark-600 rounded disabled:opacity-30">↓</button>
                        <button type="button" onClick={() => removeQuestion(index)} disabled={formData.questions.length === 1} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-30"><FaTrash /></button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Question Text</label>
                        <textarea rows={2} value={question.text} onChange={(e) => handleQuestionChange(index, 'text', e.target.value)} placeholder="Enter question text" className="w-full px-3 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Help Text (Optional)</label>
                        <input type="text" value={question.helpText} onChange={(e) => handleQuestionChange(index, 'helpText', e.target.value)} placeholder="Enter help text" className="w-full px-3 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={question.isRequired} onChange={(e) => handleQuestionChange(index, 'isRequired', e.target.checked)} className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Required Question</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-dark-700">
          <button onClick={onHide} className="px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">{mode === 'create' ? 'Create Section' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};

export default QuestionSectionModal;